import styles from './contract-details-reducer.module.scss';
import React, { Dispatch, ReactNode, useContext } from 'react';
import { useImmerReducer } from 'use-immer';
import { Draft } from 'immer';
import { v4 } from 'uuid';
import { selectedItem } from '../contract-new-license-dialog/contract-new-license-dialog';
import {
  IBaseOrderItem, IContractItem, IContractLicense, ContractOrderTypesEnum, ICustomerContract, IExistingOrderType, INewLicenseOrderItem,
  IOrderItem, OrderStateEnum, ISellableProducts, IContractInfo, IInvoiceInfo, IContractGroup, LicenseTypeEnum, ContractTypeEnum, IContractStateType
} from '../../common-types/common-types';
import { format } from 'date-fns';
import { IUserAccount } from '../../common-types/common-types';
import { getISODate } from '../../helpers/helpers';
/* eslint-disable-next-line */

const SUPPORT_GROUP_ID = "39b4e5d2-1ab3-484a-b3a9-b6240248858d";
const ContractStateContext = React.createContext<IContractStateType | undefined>(undefined);
const ContractDispatchContext = React.createContext<Dispatch<ActionTypes> | undefined>(undefined);

type ActionTypes =
  { type: 'INITIALIZE_NEW_CONTRACT', companyName?: string, contractInfo: IContractInfo, invoiceInfo: IInvoiceInfo, sellerName: string, groups: IContractGroup[], isSupport: boolean, contractType: ContractTypeEnum, isAcademic: boolean } |
  { type: 'NEW_CONTRACT_CONTRACT_INFO_CHANGE' } |
  { type: 'CONTRACT_RECEIVED', contract: ICustomerContract } |
  { type: "SEATS_CHANGE", licenseNumber: number, contractItemId: string, newValue: number } |
  { type: "NEW_SEATS_ONE_TIME_DISCOUNT_CHANGE", licenseNumber: number, orderId: string, newValue: number } |
  { type: "REMOVE_CHANGE", changeType: ContractOrderTypesEnum, licenseNumber: number, itemId: string } |
  { type: 'SELLABLE_PRODUCTS_RECEIVED', sellableProducts: ISellableProducts[] } |
  { type: "NEW_LICENSES_CREATED", newLicenseItems: Array<selectedItem> } |
  { type: "NEW_LICENSE_ITEM_CREATED", licenseNumber: number, item: selectedItem } |
  { type: "NEW_ITEM_SEATS_CHANGE", licenseNumber: number, orderId: string, newValue: number } |
  { type: "DISCOUNT_CHANGE", licenseNumber: number, contractItemId: string, newValue: number } |
  { type: "NEW_ITEM_RECURRING_DISCOUNT_CHANGE", licenseNumber: number, orderId: string, newValue: number } |
  { type: "NEW_ITEM_ONE_TIME_DISCOUNT_CHANGE", licenseNumber: number, orderId: string, newValue: number } |
  { type: "LICENSE_SAVE_CHANGES", contract: IContractStateType } |
  { type: "LICENSE_TERMINATION_CHANGE", licenseNumber: number, newDate: Date } |
  { type: "CONTRACT_INFO_SELECTED_RESPONSIBLE_CHANGED", newResponsibleEmail: string } |
  { type: "INVOICE_INFO_SELECTED_RESPONSIBLE_CHANGED", name: string } |
  { type: "INVOICE_INFO_SELECTED_COMPANY_CHANGED", newCompanyId?: number, newCompanyNumber: string } |
  { type: "CONTRACT_INFO_NOTE_CHANGE", newValue: string } |
  { type: "INVOICE_INFO_NOTE_CHANGE", newValue: string } |
  { type: "INVOICE_INFO_REFERENCE_CHANGE", newValue: string } |
  { type: "CONTRACT_EMPLOYEE_LIST_RECEIVED", employeeList: IUserAccount[] } |
  { type: "INVOICE_EMPLOYEE_LIST_RECEIVED", employeeList: IUserAccount[] } |
  { type: "INITIALIZE_EDIT_LICENSE" } |
  { type: "ORDERS_RECEIVED", orders: Array<IExistingOrderType> } |
  { type: "SALE_ACTIVATION_DATE_CHANGE", newDate: Date | null } |
  { type: "NEXT_INVOICE_DATE_CHANGE", newDate: Date } |
  { type: "SALE_MESSAGE_CHANGE", newValue: string } |
  { type: "SALE_REASON_CHANGE", newValue: { name: string, value: number } }

export function reducer(draft: Draft<IContractStateType> | undefined, action: ActionTypes) {
  console.log(action);

  const getLicense = (licenseNumber: number) => {
    const getLicensesAndModules = ((licenses: Array<IContractLicense>, l: IContractLicense) => licenses.concat(l.modules).concat(l));
    const allLicenses = draft?.groups.reduce((allLicenses: Array<IContractLicense>, g) => allLicenses.concat(g.licenses.reduce(getLicensesAndModules, [])), []);
    return allLicenses?.find(l => l.licenseNumber === licenseNumber);
  };

  const getItem = (licenseNumber: number, itemId: string) => {
    const license = getLicense(licenseNumber);
    const allItems = license?.modules.reduce((allItems, m) => allItems.concat(m.items), license.items);
    return allItems?.find(i => i.id === itemId);
  };

  const getContract = (): IContractStateType => {
    if (!draft) throw new Error('Contract must be initialized before editing.');
    return draft;
  }

  switch (action.type) {
    case "SALE_MESSAGE_CHANGE": {
      const contract = getContract();
      contract.saleMessage = action.newValue;
      return contract;
    }
    case "SALE_REASON_CHANGE": {
      const contract = getContract();
      contract.reasonForSale = action.newValue;
      return contract;
    }
    case "NEXT_INVOICE_DATE_CHANGE": {
      const contract = getContract();
      contract.contractInfo.nextInvoicingDate = getISODate(action.newDate);
      return contract;
    }
    case "INITIALIZE_NEW_CONTRACT": {
      const contractState: IContractStateType = {
        contractInfo: action.contractInfo,
        invoiceInfo: action.invoiceInfo,
        sellerName: action.sellerName,
        companyName: action.companyName,
        contractNumber: "Ny",
        orderItems: [],
        groups: action.groups,
        isLoaded: true,
        nextInvoiceCost: 0,
        isNew: true,
        isSupport: action.isSupport,
        newSaleDate: action.contractInfo.activationDate,
        isAcademic: action.isAcademic,
        contractType: action.contractType,
        saleMessage: "",
        reasonForSale: { name: "Other", value: 0 },
        changeContractInfoOrder: {
          contractNumber: "Ny",
          contractInfo: action.contractInfo,
          invoiceInfo: action.invoiceInfo,
          orderType: ContractOrderTypesEnum.ChangeInfo,
          invoiceChanges: [],
          contractInfoChanges: [],
          state: OrderStateEnum.Open,
          id: v4()
        }
      };
      return contractState;
    }
    case "NEW_CONTRACT_CONTRACT_INFO_CHANGE": {
      const contract = getContract();
      if (contract.changeContractInfoOrder) contract.contractInfo = contract.changeContractInfoOrder.contractInfo;
      if (contract.changeContractInfoOrder) contract.invoiceInfo = contract.changeContractInfoOrder.invoiceInfo;
      return contract;
    }
    case "CONTRACT_RECEIVED": {
      const employeeList = draft?.contractEmployeeList;
      const invoiceList = draft?.invoiceEmployeeList;
      const sellableProducts = draft?.sellableProducts;
      action.contract.contractInfo.nextInvoicingDate = getISODate(action.contract.contractInfo.nextInvoicingDate);
      action.contract.contractInfo.activationDate = getISODate(action.contract.contractInfo.activationDate);
      action.contract.orderItems.forEach(o => o.activationDate = new Date(o.activationDate));
      action.contract.isSupport = action.contract.contractType === ContractTypeEnum.SUPPORT;
      draft = action.contract;
      const changeContractInfoOrder = {
        contractNumber: action.contract.contractNumber,
        contractInfo: action.contract.contractInfo,
        invoiceInfo: action.contract.invoiceInfo,
        orderType: ContractOrderTypesEnum.ChangeInfo,
        activationDate: format(new Date(), "yyyy-MM-dd"),
        invoiceChanges: [],
        contractInfoChanges: [],
        state: OrderStateEnum.Open,
        id: v4()
      }
      draft.newSaleDate = getISODate();
      draft.invoiceEmployeeList = invoiceList;
      draft.contractEmployeeList = employeeList;
      draft.sellableProducts = sellableProducts;
      draft.reasonForSale = { name: "Other", value: 0 };
      if (draft) draft.isLoaded = true;
      if (draft) draft.changeContractInfoOrder = changeContractInfoOrder;
      return draft;
    }
    case "ORDERS_RECEIVED": {
      const contract = getContract();
      contract.existingOrders = action.orders;
      return contract;
    }
    case "SALE_ACTIVATION_DATE_CHANGE": {
      const contract = getContract();
      contract.newSaleDate = action.newDate ? getISODate(action.newDate) : undefined;
      return contract;
    }
    case "CONTRACT_INFO_SELECTED_RESPONSIBLE_CHANGED": {
      const contract = getContract();
      if (contract.changeContractInfoOrder) contract.changeContractInfoOrder.contractInfo.contractResponsibleEmail = action.newResponsibleEmail;
      return contract;
    }
    case "INVOICE_INFO_SELECTED_COMPANY_CHANGED": {
      const contract = getContract();
      if (contract.changeContractInfoOrder) {
        contract.changeContractInfoOrder.invoiceInfo.invoicedCompanyNumber = action.newCompanyNumber;
        if (!action.newCompanyId) {
          contract.invoiceEmployeeList = [];
          contract.changeContractInfoOrder.invoiceInfo.invoiceResponsibleName = undefined;
        }
      }
      return contract;
    }
    case "CONTRACT_INFO_NOTE_CHANGE": {
      const contract = getContract();
      if (contract.changeContractInfoOrder) {
        contract.changeContractInfoOrder.contractInfo.note = action.newValue;
      }
      return contract;
    }
    case "INVOICE_INFO_NOTE_CHANGE": {
      const contract = getContract();
      if (contract.changeContractInfoOrder) {
        contract.changeContractInfoOrder.invoiceInfo.note = action.newValue;
      }
      return contract;
    }
    case "INVOICE_INFO_REFERENCE_CHANGE": {
      const contract = getContract();
      if (contract.changeContractInfoOrder) {
        contract.changeContractInfoOrder.invoiceInfo.reference = action.newValue;
      }
      return contract;
    }
    case "INVOICE_INFO_SELECTED_RESPONSIBLE_CHANGED": {
      const contract = getContract();
      if (contract.changeContractInfoOrder) contract.changeContractInfoOrder.invoiceInfo.invoiceResponsibleName = action.name;
      return contract;
    }
    case "CONTRACT_EMPLOYEE_LIST_RECEIVED": {
      const contract = getContract();
      contract.contractEmployeeList = action.employeeList;
      return contract;
    }
    case "INVOICE_EMPLOYEE_LIST_RECEIVED": {
      const contract = getContract();
      contract.invoiceEmployeeList = action.employeeList;
      return contract;
    }
    case "SEATS_CHANGE": {
      const contract = getContract();
      const item = getItem(action.licenseNumber, action.contractItemId);
      const license = getLicense(action.licenseNumber);
      if (!item) throw new Error(`Kan ikke finne item med id ${action.contractItemId}`);

      const existingQuantity = item?.quantity || 0;
      const isModuleChange = item.licenseNumber !== action.licenseNumber;
      if (!isModuleChange) {
        license?.modules
          .reduce((items: Array<IContractItem>, m) => items.concat(m.items), [item])
          .forEach(m => createAddSeatsOrder(m, action.newValue - existingQuantity, m.parentLicenseNumber));
      }
      else {
        createAddSeatsOrder(item, action.newValue - existingQuantity, item.parentLicenseNumber);
      }
      return contract;
    }
    case "NEW_ITEM_SEATS_CHANGE": {
      const contract = getContract();
      const item = contract.orderItems?.find(i => i.id === action.orderId);
      if (item) item.quantity = action.newValue;
      return contract;
    }
    case "REMOVE_CHANGE": {
      const contract = getContract();
      removeChange(action.itemId);
      return contract;
    }
    case "SELLABLE_PRODUCTS_RECEIVED": {
      const contract = getContract();
      const filteredProducts = action.sellableProducts.filter(p => contract.isSupport ? p.groupId === SUPPORT_GROUP_ID : p.groupId !== SUPPORT_GROUP_ID);
      contract.sellableProducts = filteredProducts;
      return contract;
    }
    case "NEW_LICENSE_ITEM_CREATED": {
      const contract = getContract();
      AddItemToNewLicenseOrder(action.item, action.licenseNumber)
      return contract;
    }
    case "NEW_LICENSES_CREATED": {
      const contract = getContract();
      creatNewLicensesOrder(action.newLicenseItems);
      return contract;
    }
    case "DISCOUNT_CHANGE": {
      const contract = getContract();
      createNewDiscountOrder(action.licenseNumber, action.contractItemId, action.newValue);
      return contract;
    }
    case "NEW_ITEM_RECURRING_DISCOUNT_CHANGE": {
      const contract = getContract();
      const item = contract.orderItems.find(i => i.id === action.orderId);
      if (item) item.recurringDiscountPercent = action.newValue;
      return contract;
    }
    case "NEW_ITEM_ONE_TIME_DISCOUNT_CHANGE": {
      const contract = getContract();
      const item = contract.orderItems.find(i => i.id === action.orderId);
      if (item) item.oneTimeDiscountAmount = action.newValue;
      return contract;
    }
    case "NEW_SEATS_ONE_TIME_DISCOUNT_CHANGE": {
      const contract = getContract();
      const item = contract.orderItems.find(i => i.id === action.orderId);
      if (item) item.oneTimeDiscountAmount = action.newValue;
      return contract;
    }
    case "LICENSE_SAVE_CHANGES": {
      return action.contract;
    }
    case "INITIALIZE_EDIT_LICENSE": {
      const contract = getContract();

      return contract;
    }
    case "LICENSE_TERMINATION_CHANGE": {
      const contract = getContract();
      contract.newSaleDate = getISODate(action.newDate);
      createTerminationOrder(action.licenseNumber, action.newDate);
      return contract;
    }
    default: return draft;
  }

  function removeChange(itemId: string) {
    const contract = getContract();
    const index = contract.orderItems.findIndex((i: IOrderItem) => i.id === itemId);
    if (index >= 0) contract.orderItems.splice(index, 1);
  }

  function createAddSeatsOrder(contractItem: IContractItem, quantity: number, parentLicenseNumber?: number) {
    const contract = getContract();
    const item = createAddSeatsOrderItem(contractItem, quantity, parentLicenseNumber);
    const indexOfItem = contract.orderItems.findIndex(i => i.contractItemId === contractItem.id);
    if (indexOfItem !== -1) contract.orderItems.splice(indexOfItem, 1)
    if (item.quantity !== 0) contract.orderItems.push(item);
  }

  function createNewDiscountOrder(licenseNumber: number, contractItemId: string, newDiscount: number) {
    const contract = getContract();
    const item = createDiscountChangeOrderItem(licenseNumber, contractItemId, newDiscount);
    const indexOfItem = contract.orderItems.findIndex(i => i.contractItemId === contractItemId);
    if (indexOfItem !== -1) contract.orderItems.splice(indexOfItem, 1);
    contract.orderItems.push(item);
  }

  function createTerminationOrder(licenseNumber: number, activationDate: Date) {
    const contract = getContract();
    const item = createTerminateLicenseItem(licenseNumber, activationDate);
    const indexOfItem = contract.orderItems.findIndex(i => i.licenseNumber === licenseNumber);
    if (indexOfItem !== -1) contract.orderItems.splice(indexOfItem, 1);
    contract.orderItems.push(item);
  }

  function creatNewLicensesOrder(items: Array<selectedItem>) {
    const contract = getContract();
    const currentOrderItems = contract.orderItems.filter(o => o.type === ContractOrderTypesEnum.AddProductsToContract && o.licenseNumber < 1000);
    const hightestOrderItem = currentOrderItems.length > 0 ? currentOrderItems.reduce((prev, current) => (prev.licenseNumber > current.licenseNumber) ? prev : current)?.licenseNumber : -1;

    const mainItems = items.filter(i => !i.isModule && !i.isConnectedItem);
    const moduleItems = items.filter(i => i.isModule);
    const connectedItems = items.filter(i => i.isConnectedItem);

    const orderItems = mainItems.map((item, index) => createNewLicenseOrderItem(item, index + hightestOrderItem + 1));
    const moduleOrderItems = moduleItems.map((item) => createNewLicenseOrderItem(item, orderItems[0].licenseNumber, orderItems[0].licenseId));
    const connectedOrderItems = connectedItems.map((item) => createNewLicenseOrderItem(item, orderItems[0].licenseNumber, orderItems[0].licenseId));
    contract.orderItems = contract.orderItems.concat(orderItems).concat(moduleOrderItems).concat(connectedOrderItems);
  }

  function AddItemToNewLicenseOrder(item: selectedItem, licenseNumber: number) {
    const contract = getContract();
    const getParentId = () => {
      if (!item.isModule) return undefined;
      const parent = getLicense(licenseNumber);
      return parent?.items[0].id;
    }
    contract.orderItems = contract.orderItems.concat([createNewLicenseOrderItem(item, licenseNumber, getParentId())])
  }

  function createNewLicenseOrderItem(item: selectedItem, licenseNumber: number, parentId?: string): INewLicenseOrderItem {
    const contractItemId = v4();
    const contract = getContract();

    return {
      licenseNumber: licenseNumber,
      contractItemId: contractItemId,
      licenseId: contractItemId,
      id: v4(),
      recurringDiscountPercent: 0,
      quantity: item.quantity,
      recurringPrice: item.licenseType === LicenseTypeEnum.ONE_TIME ? 0 : item.price,
      setupBasePrice: item.licenseType === LicenseTypeEnum.ONE_TIME ? item.price : 0,
      productName: item.productName,
      vismaContractItemId: item.vismaId,
      isModule: item.isModule,
      type: ContractOrderTypesEnum.AddProductsToContract,
      groupId: item.groupId || "",
      itemType: item.licenseType,
      parentLicenseId: parentId,
      activationDate: contract.newSaleDate || new Date(),
      isConnectedItem: item.isConnectedItem
    }
  }

  function createBaseOrderItem(licenseNumber: number, itemId: string): IBaseOrderItem {
    const license = getLicense(licenseNumber);
    const newLicenseItem = draft?.orderItems.find((i) => i.id === itemId);
    const item = getItem(licenseNumber, itemId) || newLicenseItem;

    if (!item) throw `Kan ikke finne vare med id ${itemId}`;
    return {
      licenseNumber: licenseNumber,
      contractItemId: item.id,
      licenseId: item.id,
      id: v4(),
      recurringDiscountPercent: item.recurringDiscountPercent,
      quantity: item.quantity,
      recurringPrice: item.recurringPrice,
      productName: item.productName,
      vismaContractItemId: item.vismaContractItemId
    }
  }

  function createBaseOrderItemFromContractItem(item: IContractItem, parentLicenseNumber?: number) {
    return {
      licenseNumber: item.licenseNumber,
      contractItemId: item.id,
      licenseId: item.id,
      id: v4(),
      recurringDiscountPercent: item.recurringDiscountPercent,
      quantity: item.quantity,
      recurringPrice: item.recurringPrice,
      itemName: item.productName,
      productName: item.productName,
      isModule: !!parentLicenseNumber,
      parentLicenseNumber: parentLicenseNumber,
      vismaContractItemId: item.vismaContractItemId
    }
  }

  function createAddSeatsOrderItem(item: IContractItem, quantity: number, parentLicenseNumber?: number): IOrderItem {
    const base: any = createBaseOrderItemFromContractItem(item, parentLicenseNumber)
    base.type = ContractOrderTypesEnum.AddSeats;
    base.quantity = quantity;
    return base;
  }

  function createDiscountChangeOrderItem(licenseNumber: number, itemId: string, recurringDiscountPercent: number): IOrderItem {
    const base: any = createBaseOrderItem(licenseNumber, itemId);
    base.type = ContractOrderTypesEnum.ChangeRecurringDiscountPercent;
    base.recurringDiscountPercent = recurringDiscountPercent;
    return base;
  }

  function createTerminateLicenseItem(licenseNumber: number, activationDate: Date): IOrderItem {
    const license = getLicense(licenseNumber);
    const item = license?.items[0];
    if (!item) throw `Ingen items på lisens ${licenseNumber}`;
    return {
      licenseNumber: licenseNumber,
      contractItemId: item.id,
      licenseId: item.id,
      id: v4(),
      recurringDiscountPercent: item.recurringDiscountPercent,
      quantity: item.quantity,
      recurringPrice: item.recurringPrice,
      productName: item.productName,
      vismaContractItemId: item.vismaContractItemId,
      type: ContractOrderTypesEnum.TerminateLicense,
      activationDate: activationDate,
      itemType: license.itemType
    }
  }
}

const useSingleContractDispatch = () => {
  const context = useContext(ContractDispatchContext);
  if (context === undefined) {
    throw new Error("dispatch used outside of context");
  }
  return context;
};

function ContractProvider(props: {
  children: ReactNode,
}) {
  const hook = useImmerReducer<IContractStateType | undefined>(reducer, undefined);
  const [state, dispatch] = hook;
  return (
    <ContractDispatchContext.Provider value={dispatch}>
      <ContractStateContext.Provider value={state}>
        {props.children}
      </ContractStateContext.Provider>
    </ContractDispatchContext.Provider>
  )
}

export { ContractStateContext, ContractProvider, useSingleContractDispatch };